import { useEffect, useState } from "react";
import i18n from "i18next";

/* ------------------------------------------------------------------ *
 * FemCare 3.0 site-wide translations (24 languages).
 * Keys are the English text itself, so untranslated strings fall back
 * to English automatically. Translations load on demand from
 * /locales/fc3/<lng>.json. Language is chosen by, in order:
 *   1. ?lang= in the URL, 2. the visitor's own earlier choice,
 *   3. their browser language (if supported),
 *   4. their region (from the browser time zone — nothing is sent anywhere):
 *      auto-switch where English is not widely used, otherwise a
 *      one-tap suggestion bar.
 * ------------------------------------------------------------------ */

export const LANGS = [
  ["en", "English"], ["hi", "हिन्दी"], ["bn", "বাংলা"], ["ta", "தமிழ்"], ["te", "తెలుగు"], ["mr", "मराठी"],
  ["gu", "ગુજરાતી"], ["kn", "ಕನ್ನಡ"], ["ml", "മലയാളം"], ["or", "ଓଡ଼ିଆ"], ["pa", "ਪੰਜਾਬੀ"], ["ur", "اردو"],
  ["es", "Español"], ["pt", "Português"], ["fr", "Français"], ["de", "Deutsch"], ["ar", "العربية"],
  ["id", "Bahasa Indonesia"], ["tr", "Türkçe"], ["ru", "Русский"], ["vi", "Tiếng Việt"], ["ja", "日本語"],
  ["ko", "한국어"], ["zh", "中文"],
];
export const CODES = LANGS.map(([c]) => c);
const RTL = new Set(["ar", "ur"]);
const NS = "fc3";
const CHOICE_KEY = "fc_lang_choice";
const DISMISS_KEY = "fc_lang_suggest_dismissed";

export const T = (key, opts) =>
  i18n.t(key, { ns: NS, keySeparator: false, nsSeparator: false, defaultValue: key, ...(opts || {}) });

const loaded = new Set(["en"]);
let version = 0;
const listeners = new Set();
const notify = () => { version += 1; listeners.forEach((fn) => fn(version)); };

async function ensureLoaded(lng) {
  if (loaded.has(lng)) return;
  try {
    const r = await fetch(`/locales/fc3/${lng}.json`, { cache: "force-cache" });
    if (!r.ok) throw new Error(String(r.status));
    i18n.addResourceBundle(lng, NS, await r.json(), true, true);
    loaded.add(lng);
  } catch { /* keep English fallback */ }
}

function applyDir(lng) {
  try {
    document.documentElement.lang = lng;
    document.documentElement.dir = RTL.has(lng) ? "rtl" : "ltr";
  } catch { /* ignore */ }
}

export async function setLanguage(lng, { remember = true } = {}) {
  const code = CODES.includes(lng) ? lng : "en";
  await ensureLoaded(code);
  if (remember) { try { localStorage.setItem(CHOICE_KEY, code); } catch { /* ignore */ } }
  await i18n.changeLanguage(code);
  applyDir(code);
  notify();
}

/* time zone → language. AUTO = switch automatically; SUGGEST = offer it. */
const TZ_AUTO = {
  "America/Sao_Paulo": "pt", "America/Fortaleza": "pt", "America/Recife": "pt", "America/Manaus": "pt", "America/Bahia": "pt", "Europe/Lisbon": "pt",
  "America/Mexico_City": "es", "America/Bogota": "es", "America/Lima": "es", "America/Santiago": "es", "America/Argentina/Buenos_Aires": "es", "America/Caracas": "es", "Europe/Madrid": "es", "America/Guatemala": "es", "America/Montevideo": "es", "America/Guayaquil": "es", "America/La_Paz": "es", "America/Asuncion": "es",
  "Europe/Paris": "fr", "Europe/Brussels": "fr", "Africa/Abidjan": "fr", "Africa/Dakar": "fr", "Africa/Casablanca": "fr", "Africa/Tunis": "fr", "Africa/Algiers": "fr",
  "Europe/Berlin": "de", "Europe/Vienna": "de", "Europe/Zurich": "de",
  "Asia/Riyadh": "ar", "Asia/Dubai": "ar", "Africa/Cairo": "ar", "Asia/Baghdad": "ar", "Asia/Amman": "ar", "Asia/Kuwait": "ar", "Asia/Qatar": "ar", "Asia/Bahrain": "ar", "Asia/Muscat": "ar", "Africa/Tripoli": "ar", "Africa/Khartoum": "ar", "Asia/Beirut": "ar", "Asia/Damascus": "ar",
  "Asia/Jakarta": "id", "Asia/Makassar": "id", "Asia/Jayapura": "id", "Asia/Pontianak": "id",
  "Europe/Istanbul": "tr", "Europe/Moscow": "ru", "Asia/Yekaterinburg": "ru", "Asia/Novosibirsk": "ru", "Asia/Vladivostok": "ru", "Europe/Minsk": "ru", "Asia/Almaty": "ru",
  "Asia/Ho_Chi_Minh": "vi", "Asia/Saigon": "vi", "Asia/Tokyo": "ja", "Asia/Seoul": "ko",
  "Asia/Shanghai": "zh", "Asia/Chongqing": "zh", "Asia/Urumqi": "zh", "Asia/Taipei": "zh", "Asia/Hong_Kong": "zh",
};
const TZ_SUGGEST = { "Asia/Kolkata": "hi", "Asia/Calcutta": "hi", "Asia/Karachi": "ur", "Asia/Dhaka": "bn" };

function regionLanguage() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (TZ_AUTO[tz]) return { lng: TZ_AUTO[tz], mode: "auto" };
    if (TZ_SUGGEST[tz]) return { lng: TZ_SUGGEST[tz], mode: "suggest" };
  } catch { /* ignore */ }
  return null;
}

function browserLanguage() {
  const list = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || "en"]);
  for (const l of list) {
    const base = String(l).toLowerCase().split("-")[0];
    if (base === "en") return "en";
    if (CODES.includes(base)) return base;
  }
  return null;
}

let suggestion = null;
export function getSuggestion() { return suggestion; }

export async function initLanguage() {
  let url = null;
  try { url = new URLSearchParams(window.location.search).get("lang"); } catch { /* ignore */ }
  let choice = null;
  try { choice = localStorage.getItem(CHOICE_KEY); } catch { /* ignore */ }
  if (url && CODES.includes(url)) return setLanguage(url);
  if (choice && CODES.includes(choice)) return setLanguage(choice, { remember: false });
  const b = browserLanguage();
  if (b && b !== "en") return setLanguage(b, { remember: false });
  const r = regionLanguage();
  if (r && r.mode === "auto" && b !== "en") return setLanguage(r.lng, { remember: false });
  if (r && r.mode === "auto" && b === "en") {
    // English browser in a non-English region: suggest instead of forcing
    suggestion = r.lng;
  } else if (r && r.mode === "suggest") {
    suggestion = r.lng;
  }
  let dismissed = false;
  try { dismissed = localStorage.getItem(DISMISS_KEY) === "1"; } catch { /* ignore */ }
  if (dismissed) suggestion = null;
  await setLanguage(i18n.language && CODES.includes(i18n.language.split("-")[0]) ? i18n.language.split("-")[0] : "en", { remember: false });
  return undefined;
}

export function dismissSuggestion() {
  suggestion = null;
  try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  notify();
}

/* Re-render hook: returns "<lang>:<version>" — use as a React key. */
export function useFcLang() {
  const [v, setV] = useState(version);
  useEffect(() => {
    listeners.add(setV);
    return () => { listeners.delete(setV); };
  }, []);
  return `${(i18n.language || "en").split("-")[0]}:${v}`;
}

export const nameOf = (code) => (LANGS.find(([c]) => c === code) || [code, code])[1];
export const currentLang = () => (i18n.language || "en").split("-")[0];
